/**
 * Trainer-only console for the agent's global behaviour: the secret system prompt, the shared
 * Markdown playbooks, bulk playbook import from a folder or multiple `.md` files, and direct Git
 * access to the library repository. The page renders this only when `status.trainer` is true, and
 * the server independently rejects non-trainers with `FLUX_AGENT_TRAINER_ONLY`. Every change is a
 * Git commit on `main`; saves send the blob `revision` they started from, and conflicts ask the
 * trainer to reload rather than overwrite a newer edit by another trainer, a push, or the agent.
 *
 * @module apps/web/src/components/flux-agent/FluxAgentTrainerConsole
 */

import { useCallback, useEffect, useState, type FormEvent } from 'react';
import * as Tabs from '@radix-ui/react-tabs';
import {
  BookOpen,
  ChevronLeft,
  FolderUp,
  GitBranch,
  Loader2,
  Plus,
  ScrollText,
  ShieldCheck,
  Upload,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FluxAgentDocumentEditor } from './FluxAgentDocumentEditor';
import { FluxAgentGitPanel } from './FluxAgentGitPanel';
import { fluxAgentApi, shortId, type LibraryDocument } from '@/lib/fluxAgentApi';
import { buildImportDocuments, normalizeDocumentPath, type ImportPlan } from '@/lib/fluxAgentFiles';
import { cn } from '@/lib/utils';
import { useCachedState } from '@/lib/uiCache';

/** Inputs for {@link FluxAgentTrainerConsole}. */
export interface FluxAgentTrainerConsoleProps {
  /** Whether the dialog is shown. */
  open: boolean;
  /** Called with false when the trainer closes the console. */
  onOpenChange: (open: boolean) => void;
}

/** Tailwind classes shared by every console tab trigger, including the active-state styling. */
const TAB_TRIGGER =
  'flex flex-1 items-center justify-center gap-1.5 rounded px-2 py-1.5 text-xs font-medium text-muted-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm sm:text-sm';

/** Alert-styled error line shared by the console tabs.
 * @component ErrorNote
 */
function ErrorNote({
  text,
}: {
  /** Message to show; nothing renders when empty. */
  text: string | null;
}) {
  if (!text) return null;
  return (
    <p
      role="alert"
      className="border-destructive/40 bg-destructive/5 text-destructive rounded-md border px-3 py-2 text-xs"
    >
      {text}
    </p>
  );
}

/** Load, edit, and restore the secret system prompt.
 * @component SystemPromptTab
 */
function SystemPromptTab() {
  // Returning to this tab shows the prompt it last showed while the request refreshes it.
  const [document, setDocument] = useCachedState<LibraryDocument | null>(
    'flux-agent:trainer:system-prompt',
    null
  );
  const [isDefault, setIsDefault] = useCachedState(
    'flux-agent:trainer:system-prompt-default',
    false
  );
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    const result = await fluxAgentApi.getSystemPrompt();
    if (!result.ok) return setError(result.message);
    setDocument(result.data.document);
    setIsDefault(result.data.isDefault);
  }, [setDocument, setIsDefault]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!document) {
    return error ? <ErrorNote text={error} /> : <Loading />;
  }
  return (
    <div className="space-y-3">
      <ErrorNote text={error} />
      {isDefault && (
        <Badge variant="secondary" className="gap-1">
          <ShieldCheck className="h-3 w-3" /> Built-in default — saving commits the first trainer
          version
        </Badge>
      )}
      <FluxAgentDocumentEditor
        document={document}
        label="System prompt (Markdown)"
        description="Private to trainers. Applies to every user's conversations."
        withChangeNote
        onSave={(content, changeNote) =>
          fluxAgentApi.saveSystemPrompt({
            content,
            expectedRevision: document.revision ?? '',
            changeNote: changeNote || undefined,
          })
        }
        onSaved={saved => {
          setDocument(saved);
          setIsDefault(false);
        }}
        onReload={load}
        loadHistory={fluxAgentApi.systemPromptHistory}
      />
    </div>
  );
}

/** List, create, edit, delete, and review history of global playbooks.
 * @component PlaybooksTab
 */
function PlaybooksTab() {
  const [documents, setDocuments] = useState<LibraryDocument[] | null>(null);
  const [selected, setSelected] = useState<LibraryDocument | null>(null);
  const [commit, setCommit] = useState<string | null>(null);
  const [newPath, setNewPath] = useState('');
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (keepPath?: string) => {
    setError(null);
    const result = await fluxAgentApi.listPlaybooks();
    if (!result.ok) return setError(result.message);
    const sorted = [...result.data.documents].sort((a, b) => a.path.localeCompare(b.path));
    setDocuments(sorted);
    setCommit(result.data.commit);
    if (keepPath !== undefined) setSelected(sorted.find(doc => doc.path === keepPath) || null);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const create = (event: FormEvent) => {
    event.preventDefault();
    const typed = newPath.trim().replace(/^\/+/, '');
    const withFolder = typed.startsWith('playbooks/') ? typed : `playbooks/${typed}`;
    // The agent only reads Markdown under `playbooks/`, so new files always land there.
    const path = normalizeDocumentPath(
      withFolder.endsWith('.md') ? withFolder : `${withFolder}.md`
    );
    if (!path)
      return setError(
        'Use letters, numbers, dots, dashes, underscores and slashes, ending in .md.'
      );
    const existing = documents?.find(doc => doc.path === path);
    setSelected(existing || { path, content: '', revision: null });
    setNewPath('');
    setError(null);
  };

  const remove = async (document: LibraryDocument) => {
    if (!document.revision) return setSelected(null);
    if (!window.confirm(`Delete the playbook “${document.path}” for every user?`)) return;
    const result = await fluxAgentApi.deletePlaybook(document.path, document.revision);
    if (!result.ok) return setError(result.message);
    setSelected(null);
    await load();
  };

  return (
    <div className="grid min-h-0 gap-4 md:grid-cols-[15rem_1fr]">
      <div className={cn('min-h-0 space-y-3', selected && 'hidden md:block')}>
        <form onSubmit={create} className="flex gap-1.5">
          <Label htmlFor="flux-new-playbook" className="sr-only">
            New playbook path
          </Label>
          <Input
            id="flux-new-playbook"
            value={newPath}
            onChange={event => setNewPath(event.target.value)}
            placeholder="playbooks/low-days.md"
            className="h-9 font-mono text-xs"
          />
          <Button
            type="submit"
            size="sm"
            variant="outline"
            disabled={!newPath.trim()}
            aria-label="Create playbook"
            className="shrink-0"
          >
            <Plus className="h-4 w-4" />
          </Button>
        </form>
        <ErrorNote text={error} />
        {!documents ? (
          !error && <Loading />
        ) : documents.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            No playbooks yet. Create one or import a folder.
          </p>
        ) : (
          <ul
            className="native-scroll max-h-[50dvh] space-y-1 overflow-y-auto"
            aria-label="Playbooks"
          >
            {documents.map(doc => (
              <li key={doc.path}>
                <button
                  type="button"
                  onClick={() => setSelected(doc)}
                  aria-current={selected?.path === doc.path ? 'true' : undefined}
                  className={cn(
                    'focus-visible:ring-ring w-full truncate rounded-md px-3 py-2 text-left font-mono text-xs transition-colors focus-visible:outline-none focus-visible:ring-2',
                    selected?.path === doc.path
                      ? 'bg-primary text-primary-foreground'
                      : 'hover:bg-accent'
                  )}
                  title={doc.path}
                >
                  {doc.path}
                </button>
              </li>
            ))}
          </ul>
        )}
        {commit && (
          <p className="text-muted-foreground text-xs">
            Latest commit <code className="font-mono">{shortId(commit)}</code>
          </p>
        )}
      </div>
      <div className={cn('min-h-0', !selected && 'hidden md:block')}>
        {selected ? (
          <>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="mb-2 gap-1 md:hidden"
              onClick={() => setSelected(null)}
            >
              <ChevronLeft className="h-4 w-4" /> All playbooks
            </Button>
            <FluxAgentDocumentEditor
              key={selected.path}
              document={selected}
              label="Playbook (Markdown)"
              withChangeNote
              onSave={(content, changeNote) =>
                fluxAgentApi.savePlaybook(selected.path, {
                  content,
                  expectedRevision: selected.revision ?? '',
                  changeNote: changeNote || undefined,
                })
              }
              onSaved={saved => {
                setSelected(saved);
                void load();
              }}
              onReload={() => load(selected.path)}
              onDelete={() => void remove(selected)}
              loadHistory={
                selected.revision ? () => fluxAgentApi.playbookHistory(selected.path) : undefined
              }
            />
          </>
        ) : (
          documents &&
          documents.length > 0 && (
            <p className="text-muted-foreground text-sm">Choose a playbook to edit it.</p>
          )
        )}
      </div>
    </div>
  );
}

/**
 * Read picked files into an {@link ImportPlan}. Only `.md`/`.markdown` bodies are read; other files
 * get empty content and are reported as skipped by {@link buildImportDocuments}.
 */
async function readSources(files: FileList | null): Promise<ImportPlan> {
  const list = Array.from(files || []);
  const sources = await Promise.all(
    list.map(async file => ({
      relativePath:
        (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name,
      content: /\.(md|markdown)$/i.test(file.name) ? await file.text() : '',
    }))
  );
  return buildImportDocuments(sources);
}

/** Pick a folder or Markdown files, preview the resulting paths, and import them.
 * @component ImportTab
 */
function ImportTab() {
  const [plan, setPlan] = useState<ImportPlan | null>(null);
  const [replace, setReplace] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);

  const choose = async (files: FileList | null) => {
    setError(null);
    setResult(null);
    try {
      setPlan(await readSources(files));
    } catch {
      setError('Those files could not be read.');
    }
  };

  const run = async () => {
    if (!plan?.documents.length) return;
    if (
      replace &&
      !window.confirm(
        `Replace all playbooks? Existing playbooks not in these ${plan.documents.length} files will be removed for every user.`
      )
    ) {
      return;
    }
    setBusy(true);
    setError(null);
    const response = await fluxAgentApi.importPlaybooks(plan.documents, replace);
    setBusy(false);
    if (!response.ok) return setError(response.message);
    const { imported, removed, commit } = response.data;
    setResult(
      `Imported ${imported} playbook${imported === 1 ? '' : 's'}` +
        (removed ? `, removed ${removed}` : '') +
        (commit ? ` in commit ${shortId(commit)}.` : '.')
    );
    setPlan(null);
  };

  return (
    <div className="space-y-4">
      <p className="text-muted-foreground text-sm">
        Upload training material as Markdown. Paths are kept relative to the folder you choose, and
        every playbook path must start with <code className="font-mono text-xs">playbooks/</code>,
        so choose the folder that contains <code className="font-mono text-xs">playbooks/</code>{' '}
        (for example <code className="font-mono text-xs">playbooks/coaching/energy.md</code>). The
        whole import is one commit. Up to 200 files per import.
      </p>
      <div className="grid gap-2 sm:grid-cols-2">
        <label className="hover:bg-accent focus-within:ring-ring flex cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed px-3 py-4 text-sm font-medium transition-colors focus-within:ring-2">
          <FolderUp className="h-4 w-4" /> Choose a folder
          <input
            type="file"
            multiple
            className="sr-only"
            ref={element => element?.setAttribute('webkitdirectory', '')}
            onChange={event => {
              void choose(event.target.files);
              event.target.value = '';
            }}
          />
        </label>
        <label className="hover:bg-accent focus-within:ring-ring flex cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed px-3 py-4 text-sm font-medium transition-colors focus-within:ring-2">
          <Upload className="h-4 w-4" /> Choose .md files
          <input
            type="file"
            multiple
            accept=".md,.markdown,text/markdown"
            className="sr-only"
            onChange={event => {
              void choose(event.target.files);
              event.target.value = '';
            }}
          />
        </label>
      </div>

      <ErrorNote text={error} />
      {result && (
        <p
          role="status"
          className="border-primary/30 bg-primary/5 rounded-md border px-3 py-2 text-sm"
        >
          {result}
        </p>
      )}

      {plan && (
        <div className="space-y-3 rounded-md border p-3">
          <p className="text-sm font-medium">
            {plan.documents.length} playbook{plan.documents.length === 1 ? '' : 's'} ready
          </p>
          {plan.documents.length > 0 && (
            <ul
              className="native-scroll max-h-40 space-y-0.5 overflow-y-auto font-mono text-xs"
              aria-label="Files to import"
            >
              {plan.documents.map(doc => (
                <li key={doc.path} className="truncate">
                  {doc.path}
                </li>
              ))}
            </ul>
          )}
          {plan.skipped.length > 0 && (
            <details className="text-muted-foreground text-xs">
              <summary className="cursor-pointer">{plan.skipped.length} skipped</summary>
              <ul className="mt-1 space-y-0.5">
                {plan.skipped.map(reason => (
                  <li key={reason}>{reason}</li>
                ))}
              </ul>
            </details>
          )}
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={replace}
              onChange={event => setReplace(event.target.checked)}
              className="mt-0.5 h-4 w-4 accent-[hsl(var(--primary))]"
            />
            <span>
              Replace all playbooks
              <span className="text-muted-foreground block text-xs">
                Removes existing playbooks that aren’t in this import.
              </span>
            </span>
          </label>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => setPlan(null)} disabled={busy}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={run}
              disabled={busy || plan.documents.length === 0}
              className="gap-1.5"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              Import
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Inline loading indicator for console tabs.
 * @component Loading
 */
function Loading() {
  return (
    <p className="text-muted-foreground flex items-center gap-2 text-xs">
      <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
    </p>
  );
}

/** Dialog hosting the System prompt, Playbooks, Import, and Git tabs for framework trainers. */
export function FluxAgentTrainerConsole({ open, onOpenChange }: FluxAgentTrainerConsoleProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[min(94dvh,52rem)] max-w-5xl flex-col">
        <DialogHeader className="pr-8">
          <DialogTitle>Trainer console</DialogTitle>
          <DialogDescription>
            Shape how Flux Agent coaches everyone. Changes apply to all users’ new replies.
          </DialogDescription>
        </DialogHeader>
        <Tabs.Root defaultValue="system-prompt" className="flex min-h-0 flex-1 flex-col gap-3">
          <Tabs.List
            aria-label="Trainer console sections"
            className="bg-muted flex gap-1 rounded-md p-1"
          >
            <Tabs.Trigger value="system-prompt" className={TAB_TRIGGER}>
              <ScrollText className="h-4 w-4" /> System prompt
            </Tabs.Trigger>
            <Tabs.Trigger value="playbooks" className={TAB_TRIGGER}>
              <BookOpen className="h-4 w-4" /> Playbooks
            </Tabs.Trigger>
            <Tabs.Trigger value="import" className={TAB_TRIGGER}>
              <FolderUp className="h-4 w-4" /> Import
            </Tabs.Trigger>
            <Tabs.Trigger value="git" className={TAB_TRIGGER}>
              <GitBranch className="h-4 w-4" /> Git
            </Tabs.Trigger>
          </Tabs.List>
          <Tabs.Content
            value="system-prompt"
            className="native-scroll min-h-0 flex-1 overflow-y-auto focus-visible:outline-none"
          >
            <SystemPromptTab />
          </Tabs.Content>
          <Tabs.Content
            value="playbooks"
            className="native-scroll min-h-0 flex-1 overflow-y-auto focus-visible:outline-none"
          >
            <PlaybooksTab />
          </Tabs.Content>
          <Tabs.Content
            value="import"
            className="native-scroll min-h-0 flex-1 overflow-y-auto focus-visible:outline-none"
          >
            <ImportTab />
          </Tabs.Content>
          <Tabs.Content
            value="git"
            className="native-scroll min-h-0 flex-1 overflow-y-auto focus-visible:outline-none"
          >
            <FluxAgentGitPanel />
          </Tabs.Content>
        </Tabs.Root>
      </DialogContent>
    </Dialog>
  );
}
